import { useEffect, useRef, useState } from "react";
import { Bot, Send, Sparkles, User, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const SUGGESTED = [
  "What does Luminary actually do?",
  "How is this different from a market-research firm?",
  "What's in a Luminous AI brief?",
  "How do I get access to Luminous AI?",
  "Which industries do you cover?",
];

function getOrCreateSessionId() {
  if (typeof window === "undefined") return "anon";
  let id = localStorage.getItem("lum_qhelp_sid");
  if (!id) {
    id = "sid-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("lum_qhelp_sid", id);
  }
  return id;
}

export default function AIQuickHelp() {
  const [messages, setMessages] = useState([
    { who: "ai", text: "Hi — I'm Luminary's Quick Help concierge. Ask me anything about our services, our methodology, or how to engage. For deeper strategic questions, I'll point you to the right place." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [remaining, setRemaining] = useState(null);
  const scrollRef = useRef(null);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    setErr("");
    setMessages((m) => [...m, { who: "user", text: question }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/quick-help/ask", {
        question,
        session_id: getOrCreateSessionId(),
      });
      setMessages((m) => [...m, { who: "ai", text: data.answer }]);
      if (typeof data.remaining === "number") setRemaining(data.remaining);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429 && detail && typeof detail === "object") {
        setRemaining(0);
        setMessages((m) => [...m, { who: "ai", text: detail.message || "Quick Help limit reached for this session." }]);
      } else {
        setErr(formatApiError(e));
      }
    }
    setBusy(false);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  return (
    <div
      className="bg-[var(--navy)] text-white border border-[var(--navy)] flex flex-col"
      data-testid="ai-quick-help"
    >
      {/* Header */}
      <div className="px-5 sm:px-6 py-4 border-b border-white/10 flex items-center gap-3">
        <div className="w-9 h-9 bg-[var(--gold)] text-[var(--navy)] flex items-center justify-center">
          <Bot size={16} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--gold)]">AI Quick Help</div>
          <div className="font-display font-bold text-base leading-tight">Luminary Concierge</div>
        </div>
        <div className="ml-auto text-[10px] font-mono uppercase tracking-[0.18em] text-white/50">
          {remaining !== null ? `${remaining} left` : "5 / session"}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-4 min-h-[280px] max-h-[420px]"
        data-testid="quick-help-messages"
      >
        {messages.map((m, i) => (
          <div key={i} className={"flex gap-3 " + (m.who === "user" ? "flex-row-reverse" : "")}>
            <div className={"w-7 h-7 flex-shrink-0 flex items-center justify-center " + (m.who === "user" ? "bg-white text-[var(--navy)]" : "bg-[var(--gold)] text-[var(--navy)]")}>
              {m.who === "user" ? <User size={13} /> : <Sparkles size={13} />}
            </div>
            <div className={"max-w-[78%] " + (m.who === "user" ? "bg-white text-[var(--navy)]" : "bg-white/5 text-white border border-white/10")}>
              <p className="text-sm leading-relaxed px-4 py-3 whitespace-pre-line">{m.text}</p>
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex gap-3">
            <div className="w-7 h-7 flex-shrink-0 bg-[var(--gold)] text-[var(--navy)] flex items-center justify-center">
              <Sparkles size={13} />
            </div>
            <div className="bg-white/5 text-white border border-white/10 px-4 py-3 text-sm">
              <span className="inline-flex gap-1.5">
                <span className="w-1.5 h-1.5 bg-[var(--gold)] animate-pulse"></span>
                <span className="w-1.5 h-1.5 bg-[var(--gold)] animate-pulse" style={{ animationDelay: "0.2s" }}></span>
                <span className="w-1.5 h-1.5 bg-[var(--gold)] animate-pulse" style={{ animationDelay: "0.4s" }}></span>
              </span>
            </div>
          </div>
        )}
        {err && (
          <div className="flex items-start gap-2 text-sm text-[var(--gold)] bg-white/5 border border-white/10 px-3 py-2">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span className="font-mono text-[12px]">{err}</span>
          </div>
        )}
      </div>

      {/* Suggested */}
      {messages.length <= 1 && !busy && (
        <div className="px-5 sm:px-6 pb-3 flex flex-wrap gap-2" data-testid="quick-help-suggested">
          {SUGGESTED.map((q) => (
            <button
              key={q}
              onClick={() => send(q)}
              className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/80 border border-white/20 px-2.5 py-1.5 hover:bg-[var(--gold)] hover:border-[var(--gold)] hover:text-[var(--navy)] transition-colors"
              data-testid={`quick-help-suggested-${q.slice(0, 14).replace(/\W+/g, "-").toLowerCase()}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="px-5 sm:px-6 py-4 border-t border-white/10 flex items-center gap-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about modules, frameworks, trackers…"
          className="flex-1 bg-transparent border-b border-white/20 focus:border-[var(--gold)] outline-none text-sm py-2 placeholder:text-white/40 text-white"
          data-testid="quick-help-input"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="bg-[var(--gold)] text-[var(--navy)] px-3 py-2 disabled:opacity-50 hover:bg-white transition-colors"
          data-testid="quick-help-send"
          aria-label="Send"
        >
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
